import React, { useState } from "react";
import MainLayout from "../../components/layout/MainLayout";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Profile.css";

function ChangePassword() {
  const [formData, setFormData] = useState({
    old_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const toggleVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Password strength checker
  const getPasswordStrength = (password) => {
    if (!password) return null;
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { label: "Weak", color: "#ef4444", width: "25%" };
    if (score === 2) return { label: "Fair", color: "#f59e0b", width: "50%" };
    if (score === 3) return { label: "Good", color: "#3b82f6", width: "75%" };
    return { label: "Strong", color: "#10b981", width: "100%" };
  };

  const strength = getPasswordStrength(formData.new_password);

  const validate = () => {
    const newErrors = {};

    if (!formData.old_password.trim()) {
      newErrors.old_password = "Old password is required";
    }

    if (!formData.new_password) {
      newErrors.new_password = "New password is required";
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters";
    }

    if (!formData.new_password_confirmation) {
      newErrors.new_password_confirmation = "Please confirm your new password";
    } else if (formData.new_password !== formData.new_password_confirmation) {
      newErrors.new_password_confirmation = "Passwords do not match";
    }

    if (
      formData.old_password &&
      formData.new_password &&
      formData.old_password === formData.new_password
    ) {
      newErrors.new_password =
        "New password must be different from old password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await api.post("/change-password", formData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Password Updated!",
          text: response.data.message,
          timer: 2500,
          showConfirmButton: false,
        });

        // Reset form
        setFormData({
          old_password: "",
          new_password: "",
          new_password_confirmation: "",
        });
        setErrors({});
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.message || "Failed to update password";
      const serverErrors = error.response?.data?.errors || {};

      // Map server errors to fields
      if (serverErrors.old_password) {
        setErrors((prev) => ({
          ...prev,
          old_password: serverErrors.old_password[0],
        }));
      } else if (serverErrors.new_password) {
        setErrors((prev) => ({
          ...prev,
          new_password: serverErrors.new_password[0],
        }));
      } else {
        Swal.fire({ icon: "error", title: "Error", text: errMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      old_password: "",
      new_password: "",
      new_password_confirmation: "",
    });
    setErrors({});
  };

  return (
    <MainLayout>
      <div className="change-password-container">
        <div className="change-password-card">
          {/* Card Header */}
          <div className="cp-card-header">
            <div className="cp-header-icon">
              <i className="fas fa-lock"></i>
            </div>
            <div>
              <h4 className="cp-title">Change Password</h4>
              <p className="cp-subtitle">
                Update your account password to keep it secure
              </p>
            </div>
          </div>

          {/* Tips Banner */}
          <div className="cp-tips-banner">
            <i className="fas fa-shield-alt"></i>
            <span>
              Use a mix of uppercase, lowercase, numbers and special characters
              for a strong password.
            </span>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="cp-form">
            {/* Old Password */}
            <div className="cp-form-group ">
              <label className="cp-label">
                Current Password <span className="required">*</span>
              </label>
              <div className="cp-input-wrapper">
                <div
                  className={`cp-input-box ${errors.old_password ? "error" : ""}`}
                >
                  <i className="fas fa-lock cp-input-icon"></i>
                  <input
                    type={showPasswords.old ? "text" : "password"}
                    name="old_password"
                    value={formData.old_password}
                    onChange={handleInputChange}
                    placeholder="Enter current password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="toggle-eye"
                    onClick={() => toggleVisibility("old")}
                    tabIndex={-1}
                  >
                    <i
                      className={`fas ${showPasswords.old ? "fa-eye-slash" : "fa-eye"}`}
                    ></i>
                  </button>
                </div>
                {errors.old_password && (
                  <span className="cp-error-msg">
                    <i className="fas fa-exclamation-circle"></i>{" "}
                    {errors.old_password}
                  </span>
                )}
              </div>
            </div>

            {/* New Password */}
            <div className="cp-form-group">
              <label className="cp-label">
                New Password <span className="required">*</span>
              </label>
              <div className="cp-input-wrapper">
                <div
                  className={`cp-input-box ${errors.new_password ? "error" : ""}`}
                >
                  <i className="fas fa-key cp-input-icon"></i>
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="toggle-eye"
                    onClick={() => toggleVisibility("new")}
                    tabIndex={-1}
                  >
                    <i
                      className={`fas ${showPasswords.new ? "fa-eye-slash" : "fa-eye"}`}
                    ></i>
                  </button>
                </div>
                {errors.new_password && (
                  <span className="cp-error-msg">
                    <i className="fas fa-exclamation-circle"></i>{" "}
                    {errors.new_password}
                  </span>
                )}

                {/* Password Strength Bar */}
                {formData.new_password && strength && (
                  <div className="strength-bar-wrapper">
                    <div className="strength-bar-track">
                      <div
                        className="strength-bar-fill"
                        style={{
                          width: strength.width,
                          background: strength.color,
                        }}
                      ></div>
                    </div>
                    <span
                      className="strength-label"
                      style={{ color: strength.color }}
                    >
                      {strength.label}
                    </span>
                  </div>
                )}

                {/* Requirements Checklist */}
                {formData.new_password && (
                  <div className="password-requirements">
                    <RequirementItem
                      met={formData.new_password.length >= 8}
                      text="At least 8 characters"
                    />
                    <RequirementItem
                      met={/[A-Z]/.test(formData.new_password)}
                      text="One uppercase letter"
                    />
                    <RequirementItem
                      met={/[0-9]/.test(formData.new_password)}
                      text="One number"
                    />
                    <RequirementItem
                      met={/[^A-Za-z0-9]/.test(formData.new_password)}
                      text="One special character"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Confirm Password */}
            <div className="cp-form-group">
              <label className="cp-label">
                Confirm Password <span className="required">*</span>
              </label>
              <div className="cp-input-wrapper">
                <div
                  className={`cp-input-box ${errors.new_password_confirmation ? "error" : ""} ${
                    formData.new_password_confirmation &&
                    formData.new_password === formData.new_password_confirmation
                      ? "success"
                      : ""
                  }`}
                >
                  <i className="fas fa-check-double cp-input-icon"></i>
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    name="new_password_confirmation"
                    value={formData.new_password_confirmation}
                    onChange={handleInputChange}
                    placeholder="Re-enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="toggle-eye"
                    onClick={() => toggleVisibility("confirm")}
                    tabIndex={-1}
                  >
                    <i
                      className={`fas ${showPasswords.confirm ? "fa-eye-slash" : "fa-eye"}`}
                    ></i>
                  </button>
                </div>
                {errors.new_password_confirmation && (
                  <span className="cp-error-msg">
                    <i className="fas fa-exclamation-circle"></i>{" "}
                    {errors.new_password_confirmation}
                  </span>
                )}
                {formData.new_password_confirmation &&
                  formData.new_password ===
                    formData.new_password_confirmation &&
                  !errors.new_password_confirmation && (
                    <span className="cp-success-msg">
                      <i className="fas fa-check-circle"></i> Passwords match
                    </span>
                  )}
              </div>
            </div>

            {/* Buttons */}
            <div className="cp-form-actions">
              <button
                type="button"
                className="btn-cp-reset"
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="btn-spinner"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Update Password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
}

// Helper sub-component for requirements
function RequirementItem({ met, text }) {
  return (
    <div className={`req-item ${met ? "met" : ""}`}>
      <i className={`fas ${met ? "fa-check-circle" : "fa-circle"}`}></i>
      <span>{text}</span>
    </div>
  );
}

export default ChangePassword;
